import { useState, useCallback, useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { IconName } from "@similarweb/icons";

import type { ICategory } from "app/common/services/categoryService.types";

import { IndustryAnalysisQueryBarContainer } from "./components/IndustryAnalysisQueryBarContainer";
import { getCategoriesData, findSelectedCategory } from "./IndustryQueryBarDataService";
import { handleItemClick } from "./IndustryQueryBarNavigationService";
import type { ICategoriesData, ICategoryModalProps } from "./types";

export interface IIndustryAnalysisQueryBarProps {
    showOnlyCategories?: boolean;
    showOnlyCustom?: boolean;
    onItemClick?: (item: ICategory) => void;
    placeholder?: string;
    categoryModalProps?: ICategoryModalProps;
    isPartnerPage?: boolean;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
}
export const IndustryAnalysisQueryBar = ({
    showOnlyCategories,
    showOnlyCustom,
    onItemClick,
    placeholder,
    categoryModalProps,
    isPartnerPage,
    customResolveItemIcon,
}: IIndustryAnalysisQueryBarProps) => {
    const { params, state } = useCurrentStateAndParams();
    const isBlankHomePage = state.name === "digitalsuite_webmarketanalysis_home";

    const categoryParam = params.category ?? params.partnerListId;
    const primaryCategory = Array.isArray(categoryParam) ? categoryParam[0] : categoryParam;

    const [categoriesData, setCategoriesData] = useState<ICategoriesData | null>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(isBlankHomePage ?? false);

    const loadCategoriesData = useCallback(() => {
        const data = getCategoriesData(primaryCategory);
        setCategoriesData(data);
    }, [primaryCategory]);

    const handleSearchItemClick = useCallback(
        (item: ICategory) => {
            setIsSearchOpen(false);

            if (onItemClick) {
                onItemClick(item);
                return;
            }

            handleItemClick(item, { name: state.name ?? "" }, params, isBlankHomePage);
        },
        [onItemClick, state, params, isBlankHomePage],
    );

    const handleQueryBarItemClick = useCallback(() => {
        setIsSearchOpen(true);
    }, []);

    const handleSearchClose = useCallback(() => {
        setIsSearchOpen(false);
    }, []);

    useEffect(() => {
        loadCategoriesData();
    }, [loadCategoriesData]);

    if (!categoriesData) {
        return null;
    }

    const selectedCategory = findSelectedCategory(
        categoriesData.selectedCategoryId,
        categoriesData.categories,
        categoriesData.customCategories,
    );

    return (
        <IndustryAnalysisQueryBarContainer
            categoriesData={categoriesData}
            selectedCategory={selectedCategory}
            isSearchOpen={isSearchOpen}
            showOnlyCategories={showOnlyCategories}
            showOnlyCustom={showOnlyCustom}
            placeholder={placeholder}
            categoryModalProps={categoryModalProps}
            isPartnerPage={isPartnerPage}
            customResolveItemIcon={customResolveItemIcon}
            onItemClick={handleSearchItemClick}
            onQueryBarItemClick={handleQueryBarItemClick}
            onSearchClose={handleSearchClose}
        />
    );
};
